import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const UTM_STORAGE_KEY = "utmSource";

const UtmContext = createContext();

export const useUtm = () => {
  const context = useContext(UtmContext);
  if (!context) {
    throw new Error("useUtm must be used within a UtmProvider");
  }
  return context;
};

/**
 * Captures ?utm_src= from the URL on any landing page, persists it to
 * localStorage (last-touch: newer values overwrite older ones), and
 * strips it from the URL without a full reload.
 */
export const UtmProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [utmSource, setUtmSource] = useState(() => {
    try {
      return localStorage.getItem(UTM_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmSrc = params.get("utm_src");
    if (!utmSrc) return;

    try {
      localStorage.setItem(UTM_STORAGE_KEY, utmSrc);
    } catch {
      // localStorage unavailable (private mode, etc.) - ignore
    }
    setUtmSource(utmSrc);

    params.delete("utm_src");
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const value = { utmSource };

  return <UtmContext.Provider value={value}>{children}</UtmContext.Provider>;
};

export default UtmContext;
